<template>
  <v-carousel
    v-bind="$attrs"
    ref="itemSlide"
    v-model="current"
    class="border"
    hide-delimiters
    v-on="$listeners"
    @change="changeSlide"
  >
    <v-carousel-item
      v-for="(item, index) in getValue"
      :key="`carousel-item-${index}`"
    >
      <div
        v-if="item['mimetype'] === false"
        class="slide-container"
      >
        <ImageWrapper
          class="align-center ma-3"
          contain
          :src="require(`@/assets/images/icons/image-broken-variant.svg`)"
          max-height="95%"
          max-width="95%"
        />
      </div>
      <div
        v-else-if="item['mimetype'] !== undefined"
        class="slide-container"
      >
        <!--DISPLAY FOR IMAGE-->
        <div
          v-if="item['mimetype'].startsWith('image')"
          class="slide-item-wrapper"
        >
          <ImageWrapper
            contain
            :src="item.src"
            max-height="100%"
            max-width="100%"
          />
        </div>
        <!--DISPLAY FOR VIDEO-->
        <div
          v-else-if="item['mimetype'].startsWith('video')"
          class="slide-item-wrapper"
        >
          <vue-plyr
            :ref="`media-plyr-${index}`"
            :key="videoKey"
            :options="{ ...plyrVideoOptions, i18n: plyrLocale }"
          >
            <video
              :src="item.src"
              controls
              crossorigin
            />
          </vue-plyr>
        </div>
        <!--DISPLAY FOR AUDIO-->
        <div
          v-else-if="item['mimetype'].startsWith('audio')"
          class="slide-item-wrapper"
        >
          <vue-plyr
            :ref="`media-plyr-${index}`"
            :key="audioKey"
            :options="{ ...plyrAudioOptions, i18n: plyrLocale }"
          >
            <audio
              :src="item.src"
              controls
              crossorigin
            />
          </vue-plyr>
        </div>
        <!-- Display for YouTube embed -->
        <div
          v-else-if="item['mimetype'].startsWith('youtube')"
          class="slide-item-wrapper"
        >
          <vue-plyr
            :ref="`media-plyr-${index}`"
            :key="videoKey"
            :options="{ ...plyrVideoOptions, i18n: plyrLocale }"
          >
            <iframe
              :id="`youtube-video-${videoKey}-${index}`"
              :src="item.src"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </vue-plyr>
        </div>
        <!--DISPLAY FOR DOCUMENT-->
        <div
          v-else
          class="slide-item-wrapper item-document"
        >
          <ImageWrapper
            class="align-center ma-3"
            contain
            :src="document_poster"
            max-height="95%"
            max-width="95%"
          />
          <v-tooltip
            v-if="!hideDocumentName"
            bottom
          >
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                :class="{ color: `${textColor}--text` }"
                class="text-left document-item text-h5"
                v-on="on"
              >
                {{ item.name }}
              </div>
            </template>
            <span>{{ item.name }}</span>
          </v-tooltip>
        </div>
      </div>
    </v-carousel-item>
  </v-carousel>
</template>

<script>
import ImageWrapper from '@/components/base/molecules/ImageWrapper/ImageWrapper.vue'
import ColorUtils from '@/utils/ColorUtils'
export default {
  name: 'CarouselWrapper',
  components: {
    ImageWrapper
  },
  props: {
    // List of item to be displayed (with attributes)
    /**
     * List of item to be displayed (with attributes)
     * @arg {Array} value Array of files
     * @arg {String} value[].name Name of the file
     * @arg {String} value[].src Source of the file
     * @arg {String} value[].mimetype Type of the file
     * @arg {String} value[].type Type of the file // TODO: from backend or external source
     */
    value: {
      type: Array,
      default: () => { return [] }
    },
    // Current item index that need to be displayed
    currentItem: {
      type: Number,
      default: 0
    },
    // options for video player
    plyrVideoOptions: {
      type: Object,
      default: () => {}
    },
    // options for audio player
    plyrAudioOptions: {
      type: Object,
      default: () => {}
    },
    backgroundHexa: {
      type: String,
      default: '#000000cc'
    },
    // Show or hide document name on top of pic
    hideDocumentName: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      videoKey: Date.now(),
      audioKey: Date.now(),
      audio_poster: require('@/assets/images/icons/audio.png'),
      video_poster: require('@/assets/images/icons/video.png'),
      document_poster: require('@/assets/images/icons/document.png'),
      plyrLocale: this.$t('vue-plyr')
    }
  },
  computed: {
    getValue: {
      get () {
        return this.value
      },
      set (newVal) {
        if (this.value !== newVal) this.$emit('input', newVal)
      }
    },
    current: {
      get () {
        return this.currentItem
      },
      set (val) {
        this.$emit('update:currentItem', val)
      }
    },
    textColor () {
      return ColorUtils.hexa(this.backgroundHexa)
        .contrast()
        .name()
    }
  },

  watch: {
    plyrVideoOptions: {
      deep: true,
      handler () {
        this.videoKey = Date.now()
      }
    },
    plyrAudioOptions: {
      deep: true,
      handler () {
        this.audioKey = Date.now()
      }
    }
  },

  async created () {
    // fetch multiple request
    const promises = []
    const totalSlide = this.getValue.length
    for (let i = 0; i < totalSlide; i++) {
      promises.push(this.getContentType(i))
      this.getName(i)
    }
    await Promise.all(promises).then(() => {
      this.current = this.current < totalSlide ? this.current : totalSlide - 1
    })
  },
  methods: {
    changeSlide (index) {
      this.getValue.forEach((el, i) => {
        // Pause media if not current slide
        if (i !== index && el.mimetype !== false) {
          if (
            (el.mimetype.startsWith('video') ||
            el.mimetype.startsWith('audio')) &&
            this.$refs[`media-plyr-${i}`]
          ) {
            this.$refs[`media-plyr-${i}`][0].player.pause()
          } else if (el.mimetype.startsWith('youtube')) {
            const iframe = document.getElementById(`youtube-video-${this.videoKey}-${i}`)
            iframe?.contentWindow.postMessage(JSON.stringify({
              event: 'command',
              func: 'pauseVideo',
              args: []
            }), '*')
          }
        }
      })
    },
    getContentType (index) {
      if (this.getValue[index].mimetype !== undefined) {
        return null
      }
      if (this.getValue[index].src.indexOf('https://www.youtube.com') > -1) {
        this.getValue[index].mimetype = 'youtube'
        // Enable youtube JS API
        const url = new URL(this.getValue[index].src)
        url.searchParams.append('enablejsapi', 1)
        this.getValue[index].src = url.href
        return null
      }
      if (this.getValue[index].src.indexOf('https://picsum.photos') > -1) {
        this.getValue[index].mimetype = 'image'
        this.getValue[index].name = 'Picsum'
        return null
      }

      return fetch(this.getValue[index].src, { method: 'HEAD' })
        .then(response => {
          if (response?.status === 200) {
            this.getValue[index].mimetype = 'null'
            response.headers.forEach((value, key) => {
              this.$set(this.getValue[index], key, value)
            })
            return true
          } else {
            this.getValue[index].mimetype = false
            return false
          }
        })
        .catch(() => {
          this.getValue[index].name = ''
          this.getValue[index].mimetype = 'null'
        })
    },
    async getName (index) {
      if (this.getValue[index].name !== undefined) {
        return
      }
      if (this.getValue[index].mimetype === 'youtube') {
        return
      }

      const srcOnly = this.getValue[index].src.split('?')[0]
      // detect file name using regex
      const regex = /[^/\\&?#]+\.\w{3,4}(?=([?&#].*$|$))/m
      const match = regex.exec(srcOnly)
      if (match !== null) {
        this.getValue[index].name = match[0]
        return
      }

      // if file name is not detected, use default name
      this.getValue[index].name = this.$t('components.carouselWrapper.untitled')
    }
  }
}
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.25em;
}
.slide-container {
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slide-item-wrapper {
  height: 100%;
  width: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slide-item-wrapper > iframe {
  height: 100%;
  width: 100%;
}

.slide-item-wrapper .item-document {
  flex-direction: column;
}

.document-item {
  position: absolute;
  top: 0%;
  left: 0%;
  width: 100%;
  height: 3rem;
  background-image: linear-gradient(rgb(0, 0, 0, 0.2), rgb(0, 0, 0, 0));
  padding: 8px 20px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
